import { request } from '@/utils/http'

// ===================== 类型定义 =====================

// 创建用户
export interface TenantUserCreateRequest {
  avatar?: string
  username: string
  phone: string
  email: string
  wechatWorkUserid?: string // 企微ID
  englishName?: string // 英文名称
  landlineAreaCode?: string // 座机电话国家代码（如+86）
  landlineNumber?: string // 座机电话区号（如0515）
  landlineExtension?: string // 座机电话号码（如467373）
  tenantUserOrgRoleRelList?: TenantUserOrgRoleRelRequest[]
}

export interface TenantUserOrgRoleRelRequest {
  officeId?: string
  departmentIds?: string[]
  roleIds?: string[]
  isMain?: number
}

// 更新用户
export interface TenantUserUpdateRequest {
  userId: string
  avatar?: string
  username: string
  phone: string
  email: string
  wechatWorkUserid?: string // 企微ID
  englishName?: string // 英文名称
  landlineAreaCode?: string // 座机电话国家代码（如+86）
  landlineNumber?: string // 座机电话区号（如0515）
  landlineExtension?: string // 座机电话号码（如467373）
  tenantUserOrgRoleRelList?: TenantUserOrgRoleRelRequest[]
}

// 删除用户
export interface TenantUserDeleteRequest {
  userId: string
}

// 分页查询
export interface TenantUserPageRequest {
  pageNum?: number
  pageSize?: number
  sort?: string
  paging?: boolean
  keyword?: string
  status?: number // 0-待激活 1-正常 2-禁用
  roleType?: number // 1-管理员 2-普通用户
}

// 修改状态
export interface TenantUserStatusRequest {
  userId: string
  status: number
}

// 重置密码
export interface UserManagerResetPasswordRequest {
  userId: string
}

// 未绑定企业用户查询
export interface TenantUserListByIdRequest {
  userIds: string[]
}

// ===================== 响应类型 =====================

export interface OfficeVO {
  id: string
  name: string
  nameEn?: string
  code?: string
  description?: string
  parentId?: string
}

export interface DepartmentVO {
  id: string
  name: string
  nameEn?: string
  officeId?: string
  parentId?: string
}

export interface RoleVO {
  id: string
  name: string
  officeId?: string
  departmentId?: string
}

export interface TenantUserVO {
  id: string
  username: string
  realName?: string
  phone: string
  email: string
  password?: string
  avatar?: string
  userType?: number
  status: number
  roleType?: number
  tenantId?: string
  enterpriseId?: string
  enterpriseName?: string
  enterpriseNameEn?: string
  dateCreated?: string
  dateModified?: string
  loginTime?: string
  officeIds?: string[]
  departmentIds?: string[]
  roleIds?: string[]
  leaders?: TenantUserVO[]
  wechatWorkUserid?: string // 企微ID
  englishName?: string // 英文名称
  landlineAreaCode?: string // 座机电话国家代码（如+86）
  landlineNumber?: string // 座机电话区号（如0515）
  landlineExtension?: string // 座机电话号码（如467373）
}

export interface PageTenantUserVOResponse {
  list: TenantUserVO[]
  total: number
  size: number
  current: number
}

export interface UserStatusCountVO {
  total: number
  enableCount: number
  disableCount: number
  pendingCount: number
}

export interface UserResetPasswordVO {
  userId: string
  newPassword: string
  email: string
}

export interface TenantUserOrgRoleRelVO {
  officeId: string
  departmentIds: string[]
  roleIds: string[]
  isMain?: number
}

export interface TenantUserDetailRequest {
  id: string
}

export interface TenantUserDetailVO extends TenantUserVO {
  tenantUserOrgRoleRelList?: TenantUserOrgRoleRelVO[]
}

// ===================== 接口实现 =====================

/**
 * 分页查询企业用户
 */
export function pageTenantUser(data: TenantUserPageRequest) {
  return request.post<PageTenantUserVOResponse>('/enterprise/tenant/tenantUser/page', data)
}

/**
 * 创建企业用户
 */
export function createTenantUser(data: TenantUserCreateRequest) {
  return request.post('/enterprise/tenant/tenantUser/create', data)
}

/**
 * 更新企业用户
 */
export function updateTenantUser(data: TenantUserUpdateRequest) {
  return request.post('/enterprise/tenant/tenantUser/update', data)
}

/**
 * 删除企业用户
 */
export function deleteTenantUser(data: TenantUserDeleteRequest) {
  return request.post('/enterprise/tenant/tenantUser/delete', data)
}

/**
 * 修改企业用户状态
 */
export function updateTenantUserStatus(data: TenantUserStatusRequest) {
  return request.post('/enterprise/tenant/tenantUser/status', data)
}

/**
 * 重置企业用户密码
 */
export function resetTenantUserPassword(data: UserManagerResetPasswordRequest) {
  return request.post<UserResetPasswordVO>('/enterprise/tenant/tenantUser/resetPassword', data)
}

/**
 * 统计用户不同状态的数量
 */
export function getTenantUserStatusCount() {
  return request.post<UserStatusCountVO>('/enterprise/tenant/tenantUser/userStatusCount')
}

/**
 * 查询租户下所有未绑定企业的用户
 */
export function listTenantUserById(data: TenantUserListByIdRequest) {
  return request.post<TenantUserVO[]>('/enterprise/tenant/tenantUser/listById', data)
}

/**
 * 获取企业用户详情
 */
export function getTenantUserDetail(data: TenantUserDetailRequest) {
  return request.post<TenantUserDetailVO>('/enterprise/tenant/tenantUser/detail', data)
}

/**
 * 查询当前企业（租户）下的用户（CCTOWER-2652 应用中心授权用户搜索）
 *
 * 企业归属以 tenant_user_rel 关系表判定，tenant_user 仅为全量用户表
 * @param keyword 搜索关键词（用户名/姓名/邮箱/手机号），为空则返回前若干条
 */
export function listTenantUsersInEnterprise(keyword?: string) {
  return request.post<TenantUserVO[]>('/enterprise/tenant/tenantUser/listInEnterprise', {
    keyword
  })
}
